import { put, takeLatest, takeEvery, call } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { showAlert } from '../App/Assests/Utility';
import { API, graphqlOperation } from 'aws-amplify';
import packageJson from '../../package.json';
import { getReleaseBanners } from '../graphql/queries';
import { onReleaseBannerUpdate } from '../graphql/subscriptions';
import {
    publishReleaseBanner,
} from '../graphql/mutations';
import {
    fetchReleaseBannerInit,
    setReleaseBanner,
    clearReleaseBanner,
    setReleaseBannerError
} from '../redux/reducers/reducerSlices/ReleaseBannerReducer';
import {
    initReleaseBannerAdminLoading,
    setReleaseBannerAdminList,
    setReleaseBannerAdminError,
    setCloseReleaseBannerAdminForm,
} from '../redux/reducers/reducerSlices/ReleaseBannerAdminReducer';
import { sagaActions } from './sagaActions';
import { getContext } from 'redux-saga/effects';
import { ReleaseBannerPublicApi, ReleaseBannerAdminApi } from '../redux/api/agent';

/**
 * The service identifier for this application in the ReleaseBanner table.
 * Each application has its own service name in the shared ReleaseBanner DB table.
 * This application (Warehouse Portal / SchedulePro) uses "SchedulePro".
 * Other applications (e.g. IMS) use their own service identifier.
 * The `service` filter ensures this app only reacts to banners intended for it.
 */
const RELEASE_BANNER_SERVICE = 'Spro/IMS';

/**
 * The current running version of this application (from package.json).
 * Compared against `currentVersion` in the ReleaseBanner record to determine
 * whether the user's build is behind the latest release.
 */
const CURRENT_APP_VERSION = packageJson.version;

/**
 * Module-level reference to the active AppSync subscription handle.
 * Stored here so it can be unsubscribed on logout without needing Redux state.
 */
let releaseBannerSubscriptionHandle = null;

/**
 * Compares two semver-style version strings (e.g. "0.2.14" vs "1.2.14").
 * Returns true if `versionA` is strictly less than `versionB`.
 * Supports versions like "MAJOR.MINOR.PATCH".
 * @param {string} versionA - The older (current running) version
 * @param {string} versionB - The newer (released) version
 * @returns {boolean}
 */
const isNewerVersion = (versionA, versionB) => {
    if (!versionA || !versionB) return false;
    const partsA = versionA.split('.').map(Number);
    const partsB = versionB.split('.').map(Number);
    const len = Math.max(partsA.length, partsB.length);
    for (let i = 0; i < len; i++) {
        const a = partsA[i] || 0;
        const b = partsB[i] || 0;
        if (b > a) return true;
        if (b < a) return false;
    }
    return false; // equal versions — no update needed
};

/**
 * Returns true if the given banner record targets this application.
 * service is stored as a comma-separated string (e.g. "Spro/IMS,EAC").
 * We split by comma and check if RELEASE_BANNER_SERVICE is one of the values.
 */
const serviceMatchesThisApp = (service) => {
    if (!service) return false;
    return service.split(',').map(s => s.trim()).includes(RELEASE_BANNER_SERVICE);
};

/**
 * Validates a Release-type ReleaseBanner record.
 * Valid when:
 *  - service includes this app's service identifier (comma-separated check)
 *  - isActive  = truthy
 *  - isDeleted = falsy
 *  - currentVersion is strictly newer than the running app version
 *
 * @param {object} record
 * @returns {boolean}
 */
const isValidReleaseBanner = (record) => {
    if (!record) return false;
    return (
        serviceMatchesThisApp(record.service) &&
        !!record.isActive &&
        !record.isDeleted &&
        isNewerVersion(CURRENT_APP_VERSION, record.currentVersion)
    );
};

/**
 * Validates a Notification-type ReleaseBanner record.
 * Valid when:
 *  - notificationType = "Notification"
 *  - service includes this app's service identifier (comma-separated check)
 *  - isActive  = truthy
 *  - isDeleted = falsy
 *  - current UTC time is within [startDateTime, endDateTime] (open-ended if missing)
 *
 * @param {object} record
 * @returns {boolean}
 */
/**
 * Parses a datetime string as UTC.
 * Handles both ISO "YYYY-MM-DDTHH:MM:SSZ" and space-separated "YYYY-MM-DD HH:MM:SS" formats.
 * The API stores datetimes as "YYYY-MM-DD HH:MM:SS" (UTC). Appending 'Z' ensures the JS Date
 * constructor treats them as UTC rather than local time.
 */
const toUtcDate = (dtStr) => {
    if (!dtStr) return null;
    if (/[Zz]$/.test(dtStr) || /[+-]\d{2}:\d{2}$/.test(dtStr)) return new Date(dtStr);
    return new Date(dtStr.replace(' ', 'T') + 'Z');
};

const isValidNotificationBanner = (record) => {
    if (!record) return false;
    if (record.notificationType !== 'Notification') return false;
    if (!serviceMatchesThisApp(record.service)) return false;
    if (!record.isActive) return false;
    if (record.isDeleted) return false;

    const now = new Date();
    const start = toUtcDate(record.startDateTime);
    const end   = toUtcDate(record.endDateTime);
    const afterStart = start ? now >= start : true;
    const beforeEnd  = end   ? now <= end   : true;
    return afterStart && beforeEnd;
};

/**
 * From a list of release banner records, find the most applicable one.
 *
 * Priority:
 *  1. Release banners (forceUpdate first, then normal) — version-gated
 *  2. Notification banners — time-gated
 *
 * Notification banners are only shown when no active Release banner exists.
 *
 * @param {Array} records - Array of ReleaseBanner records
 * @returns {object|null}
 */
const findApplicableBanner = (records) => {
    if (!Array.isArray(records) || records.length === 0) return null;

    // 1. Check for applicable Release banners (forceUpdate first)
    const releaseBanners = records.filter(r =>
        (!r.notificationType || r.notificationType === 'Release') &&
        isValidReleaseBanner(r)
    );
    if (releaseBanners.length > 0) {
        // Prefer force-update banners
        const forced = releaseBanners.find(r => !!r.forceUpdate);
        return forced || releaseBanners[0];
    }

    // 2. Fall back to Notification banners
    const notificationBanners = records.filter(isValidNotificationBanner);
    return notificationBanners.length > 0 ? notificationBanners[0] : null;
};

// ---------------------------------------------------------------------------
// Module-level timer for re-fetching when a future Notification banner exists.
// Cleared whenever a new fetch runs or the user logs out.
// ---------------------------------------------------------------------------
let notificationReFetchTimer = null;

/**
 * Clear any pending re-fetch timer (called on new fetch or logout).
 */
const clearNotificationReFetchTimer = () => {
    if (notificationReFetchTimer) {
        clearTimeout(notificationReFetchTimer);
        notificationReFetchTimer = null;
    }
};

// ---------------------------------------------------------------------------
// Worker: Fetch the current release banner via REST API (same endpoint as admin)
// Using ReleaseBannerAdminApi.manage with action:'get' reads from the same DB table
// as the admin CRUD operations, ensuring consistency between admin and live banner.
//
// Observation 1 fix (authenticated app):
//   After fetching, inspect the full record list for any Notification banners
//   whose startDateTime is in the future. If found, schedule a re-fetch via
//   setTimeout to trigger at the earliest upcoming start time (+ 5s buffer).
//   This ensures Notification banners auto-appear without a page reload.
// ---------------------------------------------------------------------------
function* fetchReleaseBannerWorker() {
    try {
        yield put(fetchReleaseBannerInit());
        clearNotificationReFetchTimer();

        let applicable = null;
        let allRecords = [];
        try {
            const response = yield call(
                [ReleaseBannerAdminApi, ReleaseBannerAdminApi.manage],
                { action: 'get' }
            );
            // manage() returns the array directly (same shape as public endpoint)
            allRecords = Array.isArray(response) ? response : (response?.data ?? []);
            applicable = findApplicableBanner(allRecords);
        } catch (fetchError) {
            console.warn(
                '[ReleaseBannerSaga] fetchReleaseBannerWorker fetch failed:',
                fetchError?.message || fetchError
            );
        }

        if (applicable) {
            yield put(setReleaseBanner(applicable));
        } else {
            yield put(clearReleaseBanner());
        }

        // Schedule a re-fetch for the earliest upcoming Notification start window
        // so banners auto-appear when their time window opens.
        const store = yield getContext('appDispatch');
        const now = new Date();
        const futureStarts = allRecords
            .filter(r =>
                r.notificationType === 'Notification' &&
                !!r.isActive && !r.isDeleted &&
                serviceMatchesThisApp(r.service) &&
                r.startDateTime
            )
            .map(r => toUtcDate(r.startDateTime))
            .filter(d => d && d > now)
            .sort((a, b) => a - b);

        if (futureStarts.length > 0) {
            const msUntilNext = futureStarts[0] - now + 5000; // 5s buffer
            notificationReFetchTimer = setTimeout(() => {
                store.dispatch({ type: sagaActions.FETCH_RELEASE_BANNER });
            }, msUntilNext);
        }

        // Establish AppSync subscription for real-time banner updates
        yield put({ type: sagaActions.SUBSCRIBE_RELEASE_BANNER });

    } catch (error) {
        console.error('[ReleaseBannerSaga] fetchReleaseBannerWorker error:', error);
        yield put(setReleaseBannerError(error?.message || 'Failed to fetch release banner'));
        yield put({ type: sagaActions.SUBSCRIBE_RELEASE_BANNER });
    }
}

// ---------------------------------------------------------------------------
// Worker: Establish AppSync Subscription for live release banner updates
// ---------------------------------------------------------------------------
function* subscribeReleaseBannerWorker() {
    try {
        //debugger
        // Unsubscribe from any previous subscription before creating a new one
        if (releaseBannerSubscriptionHandle) {
            try {
                releaseBannerSubscriptionHandle.unsubscribe();
            } catch (_) {
                // ignore unsubscribe errors
            }
            releaseBannerSubscriptionHandle = null;
        }

        // We cannot use a saga channel here because AppSync subscription callbacks
        // are event-driven. We dispatch directly from the subscription callback
        // using the Redux store — this is the same pattern used elsewhere in this project.
        // const { store } = yield call(() =>
        //     import('../redux/store/store').then((m) => ({ store: m.default }))

        // );

        // 1. Grab the container safely from Saga's runtime environmentconst
         const store = yield getContext('appDispatch');
         const subscription = API.graphql(
            graphqlOperation(onReleaseBannerUpdate)
        ).subscribe({
            /**
             * FIX: AWS Amplify v4 subscription `next` receives the GraphQL response
             * object DIRECTLY as: { data: { onReleaseBannerUpdate: { ... } } }
             * It is NOT wrapped in a `value` key.
             *
             * The previous code used `next: ({ value }) => { ... value?.data?.onReleaseBannerUpdate }`.
             * Since `value` was always undefined, `record` was always undefined,
             * and the banner never appeared when a subscription event was received.
             *
             * Fix: Accept the full response object and read `response.data` directly.
             * Also supports Amplify v5+ shape ({ value: { data: {...} } }) defensively.
             */
            next: (response) => {
                console.log('[ReleaseBannerSaga] Subscription ping received — triggering full REST re-fetch:', response);
                // IMPORTANT: The AppSync GraphQL schema for ReleaseBanner does NOT include
                // Notification-type fields (notificationType, startDateTime, endDateTime, severity).
                // Attempting to evaluate the subscription record directly for Notification banners
                // would always fail (notificationType === undefined → treated as Release → version
                // check fails → clearReleaseBanner dispatched incorrectly on other devices).
                //
                // Fix: treat the subscription event purely as an invalidation signal ("ping").
                // Dispatch FETCH_RELEASE_BANNER to re-fetch the complete banner data from the
                // REST API, which has all fields including Notification-type fields.
                // This ensures both Release and Notification banners propagate correctly to
                // ALL connected tabs and devices when an admin creates/updates a banner.
                store.dispatch({ type: sagaActions.FETCH_RELEASE_BANNER });
            },
            error: (err) => {
                console.error('[ReleaseBannerSaga] Subscription error:', err);
                store.dispatch(setReleaseBannerError('Release banner subscription error'));
            }
        });

        // Store the subscription handle so it can be torn down on logout
        releaseBannerSubscriptionHandle = subscription;

    } catch (error) {
        console.error('[ReleaseBannerSaga] subscribeReleaseBannerWorker error:', error);
        yield put(setReleaseBannerError(error?.message || 'Failed to subscribe to release banner'));
    }
}

// ---------------------------------------------------------------------------
// Worker: Unsubscribe from AppSync Subscription (called on logout)
// Also clears any pending notification re-fetch timer.
// ---------------------------------------------------------------------------
function* unsubscribeReleaseBannerWorker() {
    try {
        // Clear the future-start re-fetch timer if one is pending
        clearNotificationReFetchTimer();

        if (releaseBannerSubscriptionHandle) {
            releaseBannerSubscriptionHandle.unsubscribe();
            releaseBannerSubscriptionHandle = null;
        }
    } catch (error) {
        console.error('[ReleaseBannerSaga] unsubscribeReleaseBannerWorker error:', error);
    }
}

// ---------------------------------------------------------------------------
// Watchers — exported for rootSaga.js
// ---------------------------------------------------------------------------

/**
 * Watcher for FETCH_RELEASE_BANNER.
 * Uses takeLatest to prevent duplicate concurrent queries on rapid re-login.
 */
export function* fetchReleaseBannerWatcher() {
    yield takeLatest(sagaActions.FETCH_RELEASE_BANNER, fetchReleaseBannerWorker);
}

/**
 * Watcher for SUBSCRIBE_RELEASE_BANNER.
 * Uses takeLatest — if subscription is dispatched multiple times only the
 * latest runs, cleanly replacing the previous subscription handle.
 */
export function* subscribeReleaseBannerWatcher() {
    yield takeLatest(sagaActions.SUBSCRIBE_RELEASE_BANNER, subscribeReleaseBannerWorker);
}

/**
 * Watcher for UNSUBSCRIBE_RELEASE_BANNER.
 * Uses takeEvery so every logout triggers a clean unsubscribe.
 */
export function* unsubscribeReleaseBannerWatcher() {
    yield takeEvery(sagaActions.UNSUBSCRIBE_RELEASE_BANNER, unsubscribeReleaseBannerWorker);
}

// ---------------------------------------------------------------------------
// Worker: Fetch the public release banner via the unauthenticated Lambda endpoint.
// Used on the login page where no session token / AppSync auth is available.
// No AppSync subscription is set up — polling is handled by ReleaseBanner.jsx
// dispatching FETCH_RELEASE_BANNER_PUBLIC on a 60-second interval.
//
// Observation 3 fix:
//   The public endpoint returns all active banner records. We pass them through
//   findApplicableBanner() which handles both Release and Notification types.
//   Response shape variations handled: array directly, { data: [...] },
//   { items: [...] }, { getReleaseBanners: [...] }.
// ---------------------------------------------------------------------------
function* fetchReleaseBannerPublicWorker() {
    try {
        yield put(fetchReleaseBannerInit());

        let applicable = null;
        try {
            const responseData = yield call(
                [ReleaseBannerPublicApi, ReleaseBannerPublicApi.getReleaseBannerPublic]
            );
            // Normalise response — handle all possible shapes from the public Lambda:
            //   1. Direct array:              [{ id, notificationType, ... }, ...]
            //   2. Wrapped in data:           { data: [...] }
            //   3. AppSync-shaped:            { getReleaseBanners: [...] }
            //   4. AppSync items:             { items: [...] }
            let records = [];
            if (Array.isArray(responseData)) {
                records = responseData;
            } else if (Array.isArray(responseData?.data)) {
                records = responseData.data;
            } else if (Array.isArray(responseData?.items)) {
                records = responseData.items;
            } else if (Array.isArray(responseData?.getReleaseBanners)) {
                records = responseData.getReleaseBanners;
            } else if (responseData?.data?.getReleaseBanners) {
                // AppSync nested: { data: { getReleaseBanners: { items: [...] } } }
                const inner = responseData.data.getReleaseBanners;
                records = Array.isArray(inner) ? inner : (inner?.items ?? []);
            }

            applicable = findApplicableBanner(records);
        } catch (fetchError) {
            console.warn(
                '[ReleaseBannerSaga] Public banner fetch failed:',
                fetchError?.message || fetchError
            );
        }

        if (applicable) {
            yield put(setReleaseBanner(applicable));
        } else {
            yield put(clearReleaseBanner());
        }
    } catch (error) {
        console.error('[ReleaseBannerSaga] fetchReleaseBannerPublicWorker error:', error);
        yield put(setReleaseBannerError(error?.message || 'Failed to fetch public release banner'));
    }
}
// Worker: Fetch release banners for admin view via REST API (API-driven filters)
// ---------------------------------------------------------------------------
function* fetchReleaseBannersForAdminWorker(action) {
    try {
        yield put(initReleaseBannerAdminLoading());
        // Build the GET payload — only include optional params when provided
        const params = { action: 'get' };
        if (action?.payload?.service !== undefined)          params.service          = action.payload.service;
        if (action?.payload?.notificationType !== undefined) params.notificationType = action.payload.notificationType;
        if (action?.payload?.isActive !== undefined)         params.isActive         = action.payload.isActive;
        if (action?.payload?.startDate)                      params.startDate        = action.payload.startDate;
        if (action?.payload?.endDate)                        params.endDate          = action.payload.endDate;
        if (action?.payload?.search)                         params.search           = action.payload.search;

        const response = yield call([ReleaseBannerAdminApi, ReleaseBannerAdminApi.manage], params);
        // requests.post already unwraps axios response.data, so `response` IS the API body.
        // The API returns the array directly: [{id, service, ...}, ...]
        const records = Array.isArray(response) ? response : (response?.data ?? []);
        yield put(setReleaseBannerAdminList(records));
    } catch (error) {
        console.error('[ReleaseBannerSaga] fetchReleaseBannersForAdminWorker error:', error);
        yield put(setReleaseBannerAdminError(error?.message || 'Failed to fetch release banners for admin'));
    }
}

/**
 * Watcher for FETCH_RELEASE_BANNER_PUBLIC.
 * Uses takeLatest — only the most recent dispatch runs (prevents duplicate calls).
 */
export function* fetchReleaseBannerPublicWatcher() {
    yield takeLatest(sagaActions.FETCH_RELEASE_BANNER_PUBLIC, fetchReleaseBannerPublicWorker);
}

/* * Watcher for FETCH_RELEASE_BANNERS_ADMIN.
 * Uses takeLatest to prevent duplicate concurrent queries.
 */
export function* fetchReleaseBannersForAdminWatcher() {
    yield takeLatest(sagaActions.FETCH_RELEASE_BANNERS_ADMIN, fetchReleaseBannersForAdminWorker);
}

// ---------------------------------------------------------------------------
// Worker: Create a release banner record via REST API
// ---------------------------------------------------------------------------
function* createReleaseBannerAdminWorker(action) {
    try {
        const payload = { action: 'create', ...action.payload };
        const response = yield call([ReleaseBannerAdminApi, ReleaseBannerAdminApi.manage], payload);
        showAlert(toast.TYPE.SUCCESS, response?.message || 'Release banner created successfully', 'light');
        yield put(setCloseReleaseBannerAdminForm(true));
        yield put({ type: sagaActions.FETCH_RELEASE_BANNERS_ADMIN });
        // Re-fetch the live banner so the admin's own session sees the new banner immediately.
        yield put({ type: sagaActions.FETCH_RELEASE_BANNER });
        // Broadcast a "ping" via the AppSync publishReleaseBanner mutation so all OTHER
        // connected tabs and devices receive the onReleaseBannerUpdate subscription event.
        // Their subscription next() handler will then dispatch FETCH_RELEASE_BANNER and
        // re-fetch the complete banner data (including Notification-type fields) from REST.
        // We use only the schema-safe fields that ReleaseBannerInput accepts.
        // Any error here is non-critical — local state is already correct from the re-fetch above.
        try {
            const record = response?.data ?? response ?? {};
            yield call(
                [API, API.graphql],
                graphqlOperation(publishReleaseBanner, {
                    input: {
                        id:        String(record.id        ?? action.payload.id        ?? ''),
                        service:   String(record.service   ?? action.payload.service   ?? ''),
                        isActive:  !!(record.isActive  ?? action.payload.isActive),
                        isDeleted: !!(record.isDeleted ?? false),
                    }
                })
            );
        } catch (publishErr) {
            console.warn('[ReleaseBannerSaga] publishReleaseBanner ping failed after create (non-critical):', publishErr?.message || publishErr);
        }
    } catch (error) {
        console.error('[ReleaseBannerSaga] createReleaseBannerAdminWorker error:', error);
        showAlert(toast.TYPE.ERROR, error?.response?.data?.message || error?.message || 'Failed to create release banner', 'light');
        yield put(setReleaseBannerAdminError(error?.message || 'Failed to create release banner'));
        yield put(setCloseReleaseBannerAdminForm(false));
    }
}

// ---------------------------------------------------------------------------
// Worker: Update a release banner record via REST API
// ---------------------------------------------------------------------------
function* updateReleaseBannerAdminWorker(action) {
    try {
        const payload = { action: 'update', ...action.payload };
        const response = yield call([ReleaseBannerAdminApi, ReleaseBannerAdminApi.manage], payload);
        showAlert(toast.TYPE.SUCCESS, response?.message || 'Release banner updated successfully', 'light');
        yield put(setCloseReleaseBannerAdminForm(true));
        yield put({ type: sagaActions.FETCH_RELEASE_BANNERS_ADMIN });
        // Re-fetch the live banner so the admin's own session sees the updated state immediately
        // (e.g. when an admin enables/disables a Notification banner via the active toggle).
        yield put({ type: sagaActions.FETCH_RELEASE_BANNER });
        // Broadcast a "ping" via the AppSync publishReleaseBanner mutation so all OTHER
        // connected tabs and devices receive the onReleaseBannerUpdate subscription event.
        // Their subscription next() handler will then dispatch FETCH_RELEASE_BANNER and
        // re-fetch the complete banner data (including Notification-type fields) from REST.
        try {
            const record = response?.data ?? response ?? {};
            yield call(
                [API, API.graphql],
                graphqlOperation(publishReleaseBanner, {
                    input: {
                        id:        String(record.id        ?? action.payload.id        ?? ''),
                        service:   String(record.service   ?? action.payload.service   ?? ''),
                        isActive:  !!(record.isActive  ?? action.payload.isActive),
                        isDeleted: !!(record.isDeleted ?? false),
                    }
                })
            );
        } catch (publishErr) {
            console.warn('[ReleaseBannerSaga] publishReleaseBanner ping failed after update (non-critical):', publishErr?.message || publishErr);
        }
    } catch (error) {
        console.error('[ReleaseBannerSaga] updateReleaseBannerAdminWorker error:', error);
        showAlert(toast.TYPE.ERROR, error?.response?.data?.message || error?.message || 'Failed to update release banner', 'light');
        yield put(setReleaseBannerAdminError(error?.message || 'Failed to update release banner'));
        yield put(setCloseReleaseBannerAdminForm(false));
    }
}

// ---------------------------------------------------------------------------
// Worker: Soft-delete a release banner record via REST API
// action.payload contains: id, updatedBy, updatedOn
// ---------------------------------------------------------------------------
function* deleteReleaseBannerAdminWorker(action) {
    try {
        const payload = { action: 'delete', ...action.payload };
        const response = yield call([ReleaseBannerAdminApi, ReleaseBannerAdminApi.manage], payload);
        showAlert(toast.TYPE.SUCCESS, response?.message || 'Release banner deleted successfully', 'light');
        yield put({ type: sagaActions.FETCH_RELEASE_BANNERS_ADMIN });
    } catch (error) {
        console.error('[ReleaseBannerSaga] deleteReleaseBannerAdminWorker error:', error);
        showAlert(toast.TYPE.ERROR, error?.response?.data?.message || error?.message || 'Failed to delete release banner', 'light');
        yield put(setReleaseBannerAdminError(error?.message || 'Failed to delete release banner'));
    }
}

export function* createReleaseBannerAdminWatcher() {
    yield takeLatest(sagaActions.CREATE_RELEASE_BANNER_ADMIN, createReleaseBannerAdminWorker);
}

export function* updateReleaseBannerAdminWatcher() {
    yield takeLatest(sagaActions.UPDATE_RELEASE_BANNER_ADMIN, updateReleaseBannerAdminWorker);
}

export function* deleteReleaseBannerAdminWatcher() {
    yield takeLatest(sagaActions.DELETE_RELEASE_BANNER_ADMIN, deleteReleaseBannerAdminWorker);
}
